/**
 * As estrelas de 1 a 5 das avaliações (docs/spec/session-clock.md, "Avaliações"). `StarInput` escolhe:
 * são cinco rádios de verdade, então o Tab entra no grupo e as setas andam entre as notas, e passar o
 * mouse mostra a nota antes do clique. `Stars` só mostra uma nota, e `StarAverage`, a média.
 */
import { useId, useState } from 'react';
import { useLanguage, useT } from '../i18n/index.js';
import { formatAverage } from '../room/clock/ratingsModel.js';
import { Icon } from './icons.js';
import './stars.css';

const NOTES = [1, 2, 3, 4, 5] as const;

export function StarInput({ value, onChange, label, disabled = false }: { value: number | null; onChange: (stars: number) => void; label: string; disabled?: boolean }) {
  const t = useT();
  const name = useId();
  const [hover, setHover] = useState<number | null>(null);
  const shown = (disabled ? null : hover) ?? value ?? 0;
  return (
    <div className={`rd-starInput${disabled ? ' is-disabled' : ''}`} role="radiogroup" aria-label={label} onMouseLeave={() => setHover(null)}>
      {NOTES.map((stars) => (
        <label key={stars} className={`rd-starInput__star${stars <= shown ? ' is-on' : ''}`} onMouseEnter={() => setHover(stars)}>
          <input type="radio" name={name} value={stars} checked={value === stars} disabled={disabled} aria-label={t(stars === 1 ? 'stars.one' : 'stars.many', { n: stars })} onChange={() => onChange(stars)} />
          <Icon name="star" size={26} strokeWidth={1.6} filled={stars <= shown} />
        </label>
      ))}
    </div>
  );
}

/** Uma nota, só para ler: "4 de 5 estrelas". */
export function Stars({ value, size = 14 }: { value: number; size?: number }) {
  const t = useT();
  return (
    <span className="rd-stars" role="img" aria-label={t('stars.of', { n: value })}>
      {NOTES.map((stars) => (
        <span key={stars} className={stars <= value ? 'is-on' : undefined}>
          <Icon name="star" size={size} strokeWidth={1.6} filled={stars <= value} />
        </span>
      ))}
    </span>
  );
}

/** A média, com uma estrela só: "★ 4,5". */
export function StarAverage({ average, label, size = 14 }: { average: number; label?: string; size?: number }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const text = formatAverage(average, language);
  return (
    <span className="rd-starAverage" role="img" aria-label={label ?? t('stars.average', { average: text })}>
      <Icon name="star" size={size} strokeWidth={1.6} filled />
      <span aria-hidden="true">{text}</span>
    </span>
  );
}
